import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { CommandService, Emitter } from '@patsnap/tahiti-core';
import type { SpecificationService } from '../specification-service';
import { SpecificationRepository } from '../specification-repository';
import { SpecificationModel } from '../../models/specification-model';
import type { SpecificationState } from '../../types/specification';
import { SpecificationStep } from '../../enums';
import { SpecificationStorageService, type SpecificationStorageServiceImpl } from '../specification-storage-service';
import { SpecificationTaskCreateRepository, type SpecificationTaskCreateRepository as ISpecificationTaskCreateRepository } from '../specification-task-create-repository';


@injectable()
export class SpecificationServiceImpl implements SpecificationService {
    private _onSpecificationStateChangeEmitter = new Emitter<SpecificationState>();
    readonly onSpecificationStateChange = this._onSpecificationStateChangeEmitter.event;

    private _isReadyEmitter = new Emitter<boolean>();
    readonly onIsReadyChange = this._isReadyEmitter.event;

    private get _currentTaskId() {
        return this.getState().taskId;
    }

    // eslint-disable-next-line max-params
    constructor(
        @inject(CommandService)
        private _commandService: CommandService,
        @inject(SpecificationRepository)
        private _specificationRepository: SpecificationRepository,
        @inject(SpecificationModel)
        private _model: SpecificationModel,
        @inject(SpecificationStorageService)
        private _storageService: SpecificationStorageServiceImpl,
        @inject(SpecificationTaskCreateRepository)
        private _taskCreateRepository: ISpecificationTaskCreateRepository,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onSpecificationStateChangeEmitter.fire(this.getState());
        this._isReadyEmitter.fire(this.isReady());
    }

    public getState(): SpecificationState {
        return this._model.modelData;
    }

    public async init(taskId: string): Promise<void> {
        if (this.isReady() && this._currentTaskId === taskId) {
            return;
        }

        this._model.update(state => ({
            ...state,
            taskId: '',
        }));

        const { step: currentStep, wireframeExtractConfig } = await this._specificationRepository.getSpecificationStatus(taskId);
        if (currentStep === SpecificationStep.Empty) {
            this._commandService.executeCommand('command:route:patent-drafting-v1');
            return;
        }
        this._model.update(state => ({
            ...state,
            taskId,
            currentStep,
            wireframeExtractConfig,
        }));
    }

    public isReady(): boolean {
        return !!this.getState().taskId;
    }

    public switchStep(step: SpecificationStep) {
        this._model.update(state => ({
            ...state,
            currentStep: step,
        }));
    }
}
